import googleSheetsIcon from "../../assets/google-sheets.png";
import useDrivePicker from "../../lib/GoogleDrivePicker/drivePicker.tsx";

// translation
import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";

import { conversationActions } from "../../store/conversation.tsx";
import { useDispatch } from "react-redux";
import { Typography, useTheme, Box } from "@mui/material";
import { Trans } from "@lingui/react/macro";
import { useGoogleLogin } from "@react-oauth/google";
import axios from "../../utils/axios.tsx";

const exampleGoodSpreadsheet: (string | number)[][] = [
  ["Account", "Month", "Category", "Amount"],
  [101, "Jan", "Sales", 100],
  [102, "Jan", "Marketing", 50],
  [103, "Feb", "Sales", 120],
  [104, "Feb", "Marketing", 60],
  [105, "Mar", "Sales", 130],
  [106, "Mar", "Marketing", 70],
];

const createSpreadsheetErrorMessageHTML = (
  fileName: string,
  errorMessage: string,
) => {
  const exampleTable = `
    <table class="tallyTable" style="border-collapse: collapse; width: 100%; margin-top: 8px; margin-bottom: 6px;">
      <thead>
        <tr>
          ${exampleGoodSpreadsheet[0].map((col) => `<th>${col}</th>`).join("")}
        </tr>
      </thead>
      <tbody>
        ${exampleGoodSpreadsheet
          .slice(1)
          .map(
            (row) =>
              `<tr>${row.map((cell) => `<td>${cell}</td>`).join("")}</tr>`,
          )
          .join("")}
      </tbody>
    </table>
  `;

  return msg({
    context: "Custom message for spreadsheet connected",
    message: `
      <p>❌ <strong>Spreadsheet <em>${fileName}</em> Needs Correction</strong></p>
      <p>Your spreadsheet has some issues that need fixing before Tally can analyze it.</p>
      <p>Here’s what’s wrong: <em>${errorMessage}</em></p>
      <p>Example of a properly structured spreadsheet you can use for reference:</p>
      ${exampleTable}
      <p>Once corrected, you can continue asking questions about your data, and Tally will provide clear, accurate insights just like with any valid spreadsheet.</p>
    `,
  });
};

interface GoogleSheetDrawerActionProps {
  closeDrawer: () => void;
}

const GoogleSheetDrawerAction: React.FC<GoogleSheetDrawerActionProps> = ({
  closeDrawer,
}) => {
  const [openPicker] = useDrivePicker();
  const dispatch = useDispatch();
  const theme = useTheme();

  const handleOpenPicker = useGoogleLogin({
    flow: "auth-code",
    scope: "https://www.googleapis.com/auth/drive.file",
    onSuccess: async ({ code }: any) => {
      // Send code to backend to exchange for access_token & refresh_token
      const res = await axios.post("/v1/exchange-google-code/", { code });
      const accessToken = res.data.access_token;

      openPicker({
        clientId: import.meta.env.VITE_GOOGLE_CLIENT_ID,
        developerKey: import.meta.env.VITE_GOOGLE_API_KEY,
        viewId: "SPREADSHEETS",
        token: accessToken,
        showUploadView: true,
        supportDrives: true,
        appId: import.meta.env.VITE_GOOGLE_APP_ID,
        viewMimeTypes: "application/vnd.google-apps.spreadsheet",
        callbackFunction: async (data: any) => {
          if (data.action === "picked") {
            const file = data.docs[0];
            const fileName = file.name;
            const fileId = file.id;

            await axios
              .post(
                "/v1/integrations/sync/",
                JSON.stringify({
                  integration_identifier: "google_sheets",
                  secret: accessToken,
                  integration_parameters: { file_id: fileId },
                  eager: true,
                }),
              )
              .then((response) => {
                let message;
                if (response.status !== 200) {
                  const rawMessage = createSpreadsheetErrorMessageHTML(
                    fileName,
                    response.statusText,
                  );
                  message = i18n._(rawMessage).replace(/\n\s+/g, " ").trim();
                } else {
                  const rawMessage = msg({
                    context: "Custom message for spreadsheet connected",
                    message: `
                      <p>✅ <strong>Spreadsheet <em>${fileName}</em> Connected Successfully</strong></p>
                      <p>Your Google Spreadsheet has been linked. You can now start asking questions about the data it contains — 
                      for example, <em>"What were my total sales last month according to ${fileName} spreadsheet?"</em> or 
                      <em>"Show trends in expenses by category from ${fileName} sheet."</em></p>
                      <p>Tally will analyze the spreadsheet and give you clear, accurate insights.</p>
                      <p>If you update the spreadsheet, just ask again — Tally always works with the latest version.</p>
                    `,
                  });

                  message = i18n._(rawMessage).replace(/\n\s+/g, " ").trim();
                }

                dispatch(
                  conversationActions.appendCustomActionResponse({
                    text: message,
                  }),
                );
              })
              .catch((error) => {
                if (error.response.data.detail !== undefined) {
                  dispatch(
                    conversationActions.appendCustomActionResponse({
                      text: error.response.data.detail,
                    }),
                  );
                }
              });
          }
        },
      });
    },
    onError: () => console.error("Google Login Failed"),
  });

  // Handle click on example button
  const handleOnClick = async () => {
    closeDrawer();
    return handleOpenPicker();
  };

  return (
    <Typography
      variant="h4"
      onClick={handleOnClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        cursor: "pointer",
        whiteSpace: "nowrap",
        fontSize: "1rem !important",
        width: "100%",
        lineHeight: "1",
        margin: "0",
        py: "6px",
        "&:hover": {
          backgroundColor: theme.palette.action.hover,
          borderRadius: "25px",
        },
      }}
    >
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: "20px",
          height: "20px",
        }}
      >
        <img
          src={googleSheetsIcon}
          alt="Google Sheets"
          style={{ width: 20, height: 20 }}
        />
      </Box>
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "left",
        }}
      >
        <Trans context={"Label for button to open google drive picker"}>
          Connect Google Sheets
        </Trans>
      </Box>
    </Typography>
  );
};

export default GoogleSheetDrawerAction;
